import React, { useState, useEffect, useRef } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SubmissionPayPal = ({ onCreateOrder, onApprove, onError }) => {
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const capabilityRef = useRef('');

  const create = async () => {
    const result = await onCreateOrder();
    if (typeof result === 'string') return result;
    capabilityRef.current = result.capability_token || '';
    return result.order_id || result.payment_id;
  };

  useEffect(() => {
    fetch(`${API_URL}/api/payments/paypal-client-id`)
      .then(res => res.json())
      .then(data => { setClientId(data.client_id); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-center py-8 text-stone-500" data-testid="submission-payment-loading">Loading payment options...</div>;
  }

  if (!clientId) {
    return <div className="text-center py-8 text-red-500" data-testid="submission-payment-error">Payment system unavailable. Please try again later.</div>;
  }

  if (clientId === 'development-mock-client') return (
    <div className="border border-slate-300 bg-slate-50 p-4" data-testid="submission-mock-payment-panel">
      <p className="text-sm font-semibold text-slate-800">Preview payment simulator</p>
      <p className="mt-1 text-sm text-slate-600">No external payment service is contacted.</p>
      <button type="button" disabled={processing} data-testid="submission-mock-payment-button" className="mt-4 w-full bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50" onClick={async () => {
        setProcessing(true);
        try { const orderId = await create(); await onApprove(orderId, capabilityRef.current); }
        catch (error) { if (onError) onError(error); }
        finally { setProcessing(false); }
      }}>{processing ? 'Completing test payment…' : 'Complete test payment'}</button>
    </div>
  );

  return (
    <PayPalScriptProvider options={{ clientId, currency: "USD" }}>
      <PayPalButtons
        style={{ layout: "vertical", shape: "rect", label: "pay" }}
        createOrder={async () => {
          try {
            return await create();
          } catch (e) {
            if (onError) onError(e);
            throw e;
          }
        }}
        onApprove={async (data) => {
          try {
            await onApprove(data.orderID, capabilityRef.current);
          } catch (e) {
            if (onError) onError(e);
          }
        }}
        onCancel={() => {
          if (onError) onError(new Error('Payment cancelled'));
        }}
        onError={(err) => {
          if (onError) onError(err);
        }}
        data-testid="submission-paypal-buttons"
      />
    </PayPalScriptProvider>
  );
};
